import { useState } from "react";

import { updateStaffSettings } from "../api/console-api";
import { useOptionalApi } from "../api/context";
import { CONSOLE_LANGUAGES, directionOf, isConsoleLanguage, LANGUAGE_NAMES, t, type ConsoleLanguage } from "../i18n";
import { chooseLanguage, useLanguage } from "../i18n/session-language";

/**
 * The header's language picker (WALLIYO-0122).
 *
 * It used to change the console for the current browser tab only, with a note
 * saying so, because "nothing saves it to the account" — which stopped being
 * true once `PUT /console/me/settings` existed. It had simply never been
 * called, so a staff member re-picked their language in every new tab and
 * every private window.
 *
 * The pick is applied immediately and saved in the background. The session
 * store still holds it, so the console does not flicker back while the request
 * is in flight, and a failed save leaves the pick working for this tab with the
 * note explaining why it did not stick.
 */
export function LanguageSwitcher({
  accountLanguage,
  onChoose = chooseLanguage,
}: {
  accountLanguage: ConsoleLanguage;
  onChoose?: (language: ConsoleLanguage) => void;
}) {
  // Null when this header is rendered outside the app, in a screen's own test.
  const api = useOptionalApi();
  const language = useLanguage();
  const [saved, setSaved] = useState<ConsoleLanguage>(accountLanguage);
  const [failed, setFailed] = useState(false);

  function pick(next: ConsoleLanguage) {
    onChoose(next);
    setFailed(false);
    if (!api) {
      return;
    }
    updateStaffSettings(api.client, { consoleLanguage: next }).then(
      ({ settings }) => setSaved(settings.consoleLanguage),
      () => setFailed(true),
    );
  }

  return (
    <span className="lang">
      <select
        aria-label={t("header.language")}
        value={language}
        onChange={(event) => {
          if (isConsoleLanguage(event.target.value)) pick(event.target.value);
        }}
      >
        {CONSOLE_LANGUAGES.map((each) => (
          <option key={each} value={each} lang={each} dir={directionOf(each)}>
            {LANGUAGE_NAMES[each]}
          </option>
        ))}
      </select>
      {failed && language !== saved ? (
        <small className="lang-note" role="note">
          {t("header.languageNotSaved")}
        </small>
      ) : null}
    </span>
  );
}
